import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DateRangePickerProps } from './DateRangePicker.types'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import { PICKER_DATE_FORMAT, config } from 'config'
import { isDateValid } from 'shared/utils'
import { Option } from 'shared/types'

export const useDateRangePicker = ({
  fromDate = null,
  toDate = null,
  onFromDateChange,
  onToDateChange,
  options,
  onOptionSelect,
  value,
  onClose,
  isOpen,
  onClear,
}: DateRangePickerProps) => {
  const maxDateRef = useRef<HTMLInputElement>(null)
  const escapeKeys = [config.ENTER_KEY, config.TAB_KEY]

  const [localFromDate, setLocalFromDate] = useState(fromDate)
  const [localToDate, setLocalToDate] = useState(toDate)
  const [selectedOption, setSelectedOption] = useState<Option['value']>()
  const { t } = useTranslation()

  const getSelectedOptionName = useCallback(
    (value: Option['value']) =>
      options?.find(option => option.value === value)?.title,
    [options]
  )

  const isFromDateValid = useMemo(() => {
    return localFromDate && isDateValid(localFromDate)
  }, [localFromDate])

  const isToDateValid = useMemo(() => {
    return localToDate && isDateValid(localToDate)
  }, [localToDate])

  const selectValue = useMemo(() => {
    if (value) {
      const name = getSelectedOptionName(value)
      return name ? [name] : []
    }
    if (selectedOption) {
      const name = getSelectedOptionName(selectedOption)
      return name ? [name] : []
    }
    if (!localToDate && !localFromDate) return []
    const fromDate = isFromDateValid
      ? localFromDate?.format(PICKER_DATE_FORMAT)
      : ''
    const toDate = isToDateValid ? localToDate?.format(PICKER_DATE_FORMAT) : ''
    if (isToDateValid && !localFromDate) return [`${t('to')} ${toDate}`]
    if (isFromDateValid && !localToDate) return [`${t('from')} ${fromDate}`]
    return isToDateValid && isFromDateValid ? [`${fromDate} - ${toDate}`] : []
  }, [
    localToDate,
    localFromDate,
    selectedOption,
    getSelectedOptionName,
    t,
    isFromDateValid,
    isToDateValid,
    value,
  ])

  const handleClearDates = () => {
    setLocalFromDate(null)
    setLocalToDate(null)
  }

  const handleFromDateChange = (date: MaterialUiPickersDate) => {
    handleClearSelectedOption()
    setLocalFromDate(date)
  }

  const handleToDateChange = (date: MaterialUiPickersDate) => {
    handleClearSelectedOption()
    setLocalToDate(date)
  }

  const handleClearSelectedOption = () => {
    setSelectedOption('')
  }

  const handleOptionSelect = (value: Option['value']) => {
    if (onOptionSelect) onOptionSelect(value)
    handleClearDates()
    setSelectedOption(value)
    if (onClose) onClose()
  }

  const onToBlur = () => {
    if (onToDateChange && localToDate && isDateValid(localToDate))
      onToDateChange(localToDate)
  }
  const onFromBlur = () => {
    if (onFromDateChange && localFromDate && isDateValid(localFromDate))
      onFromDateChange(localFromDate)
  }

  const onFromKeyDown = (event: React.KeyboardEvent) => {
    if (escapeKeys.includes(event.key)) {
      event.stopPropagation()
      event.preventDefault()
      maxDateRef.current?.focus()
    }
  }

  const onToKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === config.ENTER_KEY) {
      event.preventDefault()
      maxDateRef.current?.blur()
      handleClose()
    }
  }

  const handleClose = () => {
    if (onClose) onClose()
    if (localToDate && !isDateValid(localToDate)) setLocalToDate(null)
    if (localFromDate && !isDateValid(localFromDate)) setLocalFromDate(null)
  }

  const handleClear = () => {
    if (onClear) onClear()
    setLocalToDate(null)
    setLocalFromDate(null)
  }

  useEffect(() => {
    if (!fromDate) setLocalFromDate(null)
  }, [fromDate])

  useEffect(() => {
    if (!toDate) setLocalToDate(null)
  }, [toDate])

  useEffect(() => {
    if (!value) setSelectedOption('')
  }, [value])

  const minDate = useMemo(
    () => (fromDate !== undefined ? fromDate : localFromDate) || undefined,
    [fromDate, localFromDate]
  )

  const maxDate = useMemo(
    () => (toDate !== undefined ? toDate : localToDate) || undefined,
    [toDate, localToDate]
  )

  const error = useMemo(
    () =>
      (localToDate && !isDateValid(localToDate)) ||
      (localFromDate && !isDateValid(localFromDate))
        ? t('errors:date.wrong')
        : '',
    [localFromDate, localToDate, t]
  )

  return {
    selectValue,
    handleFromDateChange,
    handleToDateChange,
    handleClearDates,
    localFromDate,
    localToDate,
    handleOptionSelect,
    minDate,
    maxDate,
    isOpen,
    onFromKeyDown,
    onToKeyDown,
    maxDateRef,
    onToBlur,
    onFromBlur,
    error,
    handleClose,
    handleClear,
  }
}
